import clsx from "clsx";
import React, { ComponentPropsWithoutRef, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { WithTestId } from "../../types";

const defaultStyle =
  "bg-transparent text-neutral-400 hover:text-body cursor-pointer rounded-full";

type IconButtonProps = ComponentPropsWithoutRef<"button"> & {
  name: string; // font awesome icon name
  style?: "solid" | "light" | "duotone"; // font awesome icon pack styling
  size?: "xs" | "sm" | "md" | "lg" | "xl"; // font awesome icon sizes
  altText: string;
  isActive?: boolean;
};

const IconButton = ({
  onClick,
  name,
  style = "solid",
  size = "md",
  disabled,
  className,
  altText,
  isActive = false,
  ...buttonProps
}: WithTestId<IconButtonProps>) => {
  const buttonClasses = useMemo(
    () =>
      twMerge(
        clsx(defaultStyle, className, {
          "opacity-50": disabled,
          "bg-neutral-400 rounded-md": isActive,
        }),
      ),
    [className, disabled, isActive],
  );

  const iconClasses = `fa-${name} fa-${style} fa-${size}`;

  const onClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  return (
    <button
      data-component="IconButton"
      onClick={onClickHandler}
      className={buttonClasses}
      title={altText}
      aria-label={altText}
      {...buttonProps}
    >
      {iconClasses && <i className={iconClasses}></i>}
    </button>
  );
};

export default IconButton;
